import React, { useCallback, useMemo, useRef, useState } from "react";
import { generatePath } from "react-router-dom";
import {
  Activity,
  CheckCircle2,
  ChevronRight,
  Globe,
  Plus,
  ShieldAlert,
  ShieldCheck,
} from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import {
  ADD_APP_PAGE,
  NO_TESTS_PHASE_VALUE,
  TERMINAL_TEST_STATUSES,
  RISK_RATING_CRITICAL,
  RISK_RATING_HIGH,
  RISK_RATING_INFO,
  RISK_RATING_LOW,
  RISK_RATING_MED,
  SINGLE_WEBAPP_PAGE,
  VULNERABILITY_PAGE,
} from "@/constants/definitions";
import UserClass from "@/models/user";
import VulnerabilityClass from "@/models/vulnerability";
import WebApplicationClass from "@/models/webApplication";
import SortSelect from "@/components/common/SortSelect";
import { deriveAppAbbrev, deriveAppColor } from "@/utils/appIdentity";
import { getTestResultLabel } from "@/utils/testStatusLabel";
import AppStatusPill from "@/components/common/AppStatusPill";
import { hasAppBeenTested } from "@/utils/webappTestStatus";
import { isWebAppTestRunning } from "@/utils/liveTestState";
import {
  getSeverityPillStyle,
  getSeverityTileStyle,
  getSeverityFilterActiveClass,
  getSeverityLabel,
} from "@/utils/severityStyles";
import { formatLongDate, formatShortDate } from "@/utils/date";
import { useAppViewScope } from "@/utils/useAppViewScope";

// ----------------------------------------
//   Constants
// ----------------------------------------


// Status pill labels + colors come from the shared
// getTestResultLabel helper (src/utils/testStatusLabel.ts) so every
// surface (this card, the Applications row, the WebApplication page
// TestDetails badge, the TestSelector dropdown) speaks with one
// voice. The earlier per-page PHASE_STYLES maps drifted and
// disagreed on edge cases — e.g. AlphaInvest reading "Test Failed"
// on the Applications row (gray, missing entry) and "Full Test" on
// the WebApplication page (driven by testStatus alone, missing the
// endTrigger nuance the BE uses for the phase computation).

/**
 * Maximum number of points rendered in the per-app sparkline. The series
 * is one entry per distinct vuln-count state (see deriveTestEventSeries),
 * so this caps how many recent test events the chart shows — not weeks.
 */
const SPARKLINE_MAX_POINTS = 12;

// hasAppBeenTested moved to src/utils/webappTestStatus.ts — imported
// at the top of this file so the Dashboard and Applications pages
// read from the same definition.

// ----------------------------------------
//   Sparkline SVG
// ----------------------------------------

const SPARKLINE_WIDTH = 96;
const SPARKLINE_HEIGHT = 28;

type SparklineTrend = "rising" | "falling" | "steady" | "empty";

// Collapse the BE's weekly carry-forward array into one point per
// distinct vuln-count state — a usable approximation of "per test
// event" without needing a BE change.
//
// The BE updates vulnHistory weekly via CRON; in weeks where no test
// runs, the previous count is carried forward. So [0,0,0,5,5,5,8,8,3]
// for a user who tested at weeks 4, 7, 9 becomes [0,5,8,3]. Leading
// zeros (pre-first-test weeks) are dropped so they don't bias the
// trend toward "rising" for every freshly-tested app.
//
// Known imprecision: two back-to-back tests that happen to land on the
// same vuln count merge into a single point. That's acceptable for an
// at-a-glance card sparkline; for accurate per-test plotting we'd
// need the BE to emit a per-test series (see follow-up).
function deriveTestEventSeries(weeklyCarryForward: number[]): number[] {
  const firstNonZero = weeklyCarryForward.findIndex((v) => v > 0);
  if (firstNonZero === -1) return [];
  const trimmed = weeklyCarryForward.slice(firstNonZero);

  const deduped: number[] = [];
  for (const value of trimmed) {
    if (deduped.length === 0 || deduped[deduped.length - 1] !== value) {
      deduped.push(value);
    }
  }
  return deduped;
}

// Direction of travel across the rendered points. Drives both the line
// color inside Sparkline and the human-readable caption next to it on
// the card, so the two stay in lockstep — bumping the rising/falling
// threshold here automatically updates the label.
function getSparklineTrend(series: number[]): SparklineTrend {
  const points = series.slice(-SPARKLINE_MAX_POINTS);
  if (points.length < 2) return "empty";
  const first = points[0];
  const last = points[points.length - 1];
  if (last > first) return "rising";
  if (last < first) return "falling";
  return "steady";
}

function getSparklineTrendLabel(trend: SparklineTrend): string {
  if (trend === "rising") return "Vulns rising";
  if (trend === "falling") return "Vulns falling";
  if (trend === "steady") return "Vulns steady";
  return "No data";
}

function Sparkline({ data }: { data: number[] }) {
  // The empty-state ("not enough points to draw a line") is rendered
  // as an empty card row by the parent — see ApplicationCard. Sparkline
  // itself just opts out rather than showing a "No data" placeholder,
  // because the placeholder + a "No data" caption next to it read as
  // noise on a fresh app's card.
  const points = data.slice(-SPARKLINE_MAX_POINTS);
  if (points.length < 2) return null;

  const maxVal = Math.max(...points, 1);
  const padding = 2;
  const innerWidth = SPARKLINE_WIDTH - padding * 2;
  const innerHeight = SPARKLINE_HEIGHT - padding * 2;

  const coords = points.map((value, index) => {
    const x = padding + (index / (points.length - 1)) * innerWidth;
    const y = padding + innerHeight - (value / maxVal) * innerHeight;
    return `${x},${y}`;
  });

  const trend = getSparklineTrend(data);
  const strokeColor =
    trend === "rising" ? "#ef4444" :
    trend === "falling" ? "#10b981" :
    "#94a3b8";

  return (
    <svg width={SPARKLINE_WIDTH} height={SPARKLINE_HEIGHT} className="shrink-0">
      <polyline
        points={coords.join(" ")}
        fill="none"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ----------------------------------------
//   Types
// ----------------------------------------

interface FlatVuln {
  vuln: VulnerabilityClass;
  app: WebApplicationClass;
  testId: string;
}

type AppSortKey = "risk-desc" | "risk-asc" | "name-asc" | "last-test-desc" | "last-test-asc";

const APP_SORT_OPTIONS: { value: AppSortKey; label: string }[] = [
  { value: "risk-desc",      label: "Risk Score (high → low)" },
  { value: "risk-asc",       label: "Risk Score (low → high)" },
  { value: "name-asc",       label: "Name (A → Z)" },
  { value: "last-test-desc", label: "Last Test (newest first)" },
  { value: "last-test-asc",  label: "Last Test (oldest first)" },
];

// ----------------------------------------
//   Props
// ----------------------------------------

interface DashboardProps {
  user: UserClass;
  listOfWebapps: WebApplicationClass[];
  isLoadingApps: boolean;
  updateCurrentWebAppCallback: (webappid: string) => void;
  navigate: (path: string) => void;
}

// ----------------------------------------
//   Component
// ----------------------------------------

export default function Dashboard({
  user,
  listOfWebapps,
  isLoadingApps,
  updateCurrentWebAppCallback,
  navigate,
}: DashboardProps) {
  const vulnTableRef = useRef<HTMLDivElement>(null);

  // My Apps / All Apps toggle — admin/owner only. Mirrors the same
  // affordance on the Applications page so the two surfaces stay
  // consistent. Persisted per-user in localStorage via useAppViewScope
  // so toggling on one page carries over to the other and survives
  // reloads.
  const [displayMyApps, setDisplayMyApps] = useAppViewScope(user.emailaddress);

  // visibleWebapps drives every per-app aggregate on the Dashboard —
  // summary tiles, app cards, the Open Vulnerabilities
  // table, the category breakdown, the Active Tests banner.
  // listOfWebapps is preserved separately for "are there any apps at
  // all" checks (isLoading / isEmpty / hasApps) so the empty-state
  // copy reflects organization-level reality, not just the current
  // filter scope.
  const visibleWebapps = useMemo(() => {
    if (displayMyApps && user.isUserAdmin()) {
      return listOfWebapps.filter(
        (app) => app.owner === user.emailaddress,
      );
    }
    return listOfWebapps;
  }, [listOfWebapps, displayMyApps, user]);

  const scrollToVulnTable = useCallback(() => {
    vulnTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const navigateToApp = useCallback(
    (webappid: string) => {
      updateCurrentWebAppCallback(webappid);
      navigate(generatePath(SINGLE_WEBAPP_PAGE, { webappid }));
    },
    [updateCurrentWebAppCallback, navigate]
  );

  const navigateToVulns = useCallback(
    (webappid: string, testid: string, vulnid?: string) => {
      updateCurrentWebAppCallback(webappid);
      // The Vulnerability page selects the vuln to display from the
      // ?vulnid= search param. Without it, it falls back to a default
      // empty vuln instance ("Unnamed vulnerability", info severity).
      const path = generatePath(VULNERABILITY_PAGE, { webappid, testid });
      const url = vulnid
        ? `${path}?vulnid=${encodeURIComponent(vulnid)}`
        : path;
      navigate(url);
    },
    [updateCurrentWebAppCallback, navigate]
  );

  // ---- Summary stats ----
  // All per-app aggregations source from visibleWebapps so the filter
  // toggle (My Apps / All Apps) affects every count on the page.
  //
  // Apps that haven't had a meaningful test yet (hasAppBeenTested =
  // false) are excluded from the severity totals. Otherwise a stale or
  // failed-test aggregate (e.g. a ServerUnreachable finding) would leak
  // into the dashboard summary while the same app's card displays a
  // "Not Tested" pill — exactly the contradiction reported.
  const summary = useMemo(() => {
    const testedApps = visibleWebapps.filter(hasAppBeenTested);
    const totalCrits = testedApps.reduce((sum, app) => sum + (app.webAppTestStatus?.opencrits ?? 0), 0);
    const totalHighs = testedApps.reduce((sum, app) => sum + (app.webAppTestStatus?.openhighs ?? 0), 0);
    const totalMeds  = testedApps.reduce((sum, app) => sum + (app.webAppTestStatus?.openmeds  ?? 0), 0);
    const totalLows  = testedApps.reduce((sum, app) => sum + (app.webAppTestStatus?.openlows  ?? 0), 0);

    const unverifiedCount = visibleWebapps.filter((app) => !app.ownershipVerified).length;

    let latestTestDate: Date | null = null;
    let latestTestAppName: string | null = null;
    for (const app of visibleWebapps) {
      const d = app.webAppTestStatus?.lastTestDate;
      if (d && d.getTime() > 0 && (!latestTestDate || d > latestTestDate)) {
        latestTestDate = d;
        latestTestAppName = app.name || null;
      }
    }

    return {
      totalCrits,
      totalHighs,
      totalMeds,
      totalLows,
      unverifiedCount,
      latestTestDate,
      latestTestAppName,
    };
  }, [visibleWebapps]);

  // ---- App card sorting ----
  const [appSortKey, setAppSortKey] = useState<AppSortKey>("risk-desc");

  const sortedApps = useMemo(() => {
    // Unverified apps always sit below verified ones — their risk score
    // is necessarily 0 (no tests can run until ownership is verified),
    // and mixing them in with completed-zero-vuln apps in the same
    // sort key was confusing (e.g. a verified app with no findings
    // sat next to an unverified app with the same "0 risk", which
    // implies they're equivalent — they aren't). The user's chosen
    // sort key applies within each group.
    const getLastTestMs = (app: WebApplicationClass) => {
      const date = app.webAppTestStatus?.lastTestDate;
      return date && date.getTime() > 0 ? date.getTime() : 0;
    };

    const applySort = (apps: WebApplicationClass[]) => {
      switch (appSortKey) {
        case "risk-desc":
          return apps.sort(
            (a, b) =>
              (b.webAppTestStatus?.riskscore ?? 0) -
              (a.webAppTestStatus?.riskscore ?? 0)
          );
        case "risk-asc":
          return apps.sort(
            (a, b) =>
              (a.webAppTestStatus?.riskscore ?? 0) -
              (b.webAppTestStatus?.riskscore ?? 0)
          );
        case "name-asc":
          return apps.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
          );
        case "last-test-desc":
          return apps.sort((a, b) => getLastTestMs(b) - getLastTestMs(a));
        case "last-test-asc":
          // Apps with no test (0) sort last for "oldest first" so they
          // don't dominate the top.
          return apps.sort((a, b) => {
            const aMs = getLastTestMs(a);
            const bMs = getLastTestMs(b);
            if (aMs === 0 && bMs === 0) return 0;
            if (aMs === 0) return 1;
            if (bMs === 0) return -1;
            return aMs - bMs;
          });
        default:
          return apps;
      }
    };

    const verified: WebApplicationClass[] = [];
    const unverified: WebApplicationClass[] = [];
    for (const app of visibleWebapps) {
      if (app.ownershipVerified) verified.push(app);
      else unverified.push(app);
    }

    return [...applySort(verified), ...applySort(unverified)];
  }, [visibleWebapps, appSortKey]);

  // ---- All vulns, sorted: crits first, then highs, then meds, then lows ----
  // Apps that haven't had a meaningful test are skipped. For tested
  // apps we iterate the latest TERMINAL test's vulns — Completed,
  // Invalid, or Failed. In-flight tests (Provisioning / Running /
  // Stopping) are deliberately skipped: per the single-source-of-truth
  // principle, the dashboard's current-state surfaces (summary tile,
  // app card pills, this table, the category breakdown) all source
  // from the latest terminal test. Live findings stream onto the
  // webapp page during a test; the dashboard reflects them once the
  // test reaches terminal and the BE aggregate updates.
  //
  // No vulnStatus filter: in v1 every vuln on the latest test is
  // current state by definition (status is no longer user-mutable and
  // the BE emits Open for new findings). Older records may carry an
  // empty or legacy vulnStatus value. Info-rated entries are still
  // excluded — they're observations rather than vulnerabilities and
  // have their own surface.
  const allVulns = useMemo(() => {
    const vulns: FlatVuln[] = [];

    for (const app of visibleWebapps) {
      if (!hasAppBeenTested(app)) continue;
      // Find the latest terminal test on this app. Post-
      // DashboardPreload BE deploy, tests[0] IS the terminal (the
      // handler returns the latest terminal in the testid/vulns
      // slot). The defensive find() handles the transitional window
      // (legacy handler returning a Running test in tests[0]) and the
      // session-local case where the user just clicked Start (tests[0]
      // is the new in-flight placeholder, prior terminal sits below).
      const terminalTest = app.tests?.find(
        (test) =>
          !!test.testStatus && TERMINAL_TEST_STATUSES.has(test.testStatus),
      );
      if (!terminalTest?.vulns?.length) continue;
      const testId = terminalTest.id;

      for (const vuln of terminalTest.vulns) {
        if (vuln.riskRating === RISK_RATING_INFO) continue;
        vulns.push({ vuln, app, testId });
      }
    }

    // Primary sort by risk severity (high → med → low), secondary by app name
    // (case-insensitive, locale-aware) so vulns from the same application cluster
    // together within each severity band.
    const severityOrder: Record<string, number> = {
      [RISK_RATING_CRITICAL]: 0,
      [RISK_RATING_HIGH]: 1,
      [RISK_RATING_MED]: 2,
      [RISK_RATING_LOW]: 3,
    };
    vulns.sort((a, b) => {
      const severityDiff =
        (severityOrder[a.vuln.riskRating] ?? 4) - (severityOrder[b.vuln.riskRating] ?? 4);
      if (severityDiff !== 0) return severityDiff;
      return (a.app.name || "").localeCompare(b.app.name || "", undefined, { sensitivity: "base" });
    });

    return vulns;
  }, [visibleWebapps]);

  // Severity counts for filter pills
  const vulnCounts = useMemo(() => {
    let critCount = 0;
    let highCount = 0;
    let medCount = 0;
    let lowCount = 0;
    for (const { vuln } of allVulns) {
      if (vuln.riskRating === RISK_RATING_CRITICAL) critCount++;
      else if (vuln.riskRating === RISK_RATING_HIGH) highCount++;
      else if (vuln.riskRating === RISK_RATING_MED) medCount++;
      else lowCount++;
    }
    return { critCount, highCount, medCount, lowCount };
  }, [allVulns]);

  // Severity filter state: null = show all
  const [severityFilter, setSeverityFilter] = useState<string | null>(null);

  const filteredVulns = useMemo(() => {
    if (!severityFilter) return allVulns;
    return allVulns.filter(({ vuln }) => vuln.riskRating === severityFilter);
  }, [allVulns, severityFilter]);

  // ---- Active tests ----
  const activeTestApps = useMemo(() => {
    return visibleWebapps.filter((app) => isWebAppTestRunning(app));
  }, [visibleWebapps]);

  // ---- Vuln category breakdown (across all current-state vulns) ----
  const categoryBreakdown = useMemo(() => {
    if (allVulns.length === 0) return [];

    const counts = new Map<string, number>();
    for (const { vuln } of allVulns) {
      const category = vuln.vulnCategory || "Uncategorised";
      counts.set(category, (counts.get(category) ?? 0) + 1);
    }

    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([category, count]) => ({ category, count }));
  }, [allVulns]);

  const isLoading  = isLoadingApps && listOfWebapps.length === 0;
  const isEmpty    = !isLoadingApps && listOfWebapps.length === 0;
  const hasApps    = listOfWebapps.length > 0;
  const hasNoVulns = allVulns.length === 0;

  // ----------------------------------------
  //   Render
  // ----------------------------------------

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gray-50">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 flex flex-col gap-6">

        {/* Header — h1 + subtitle on the left, My Apps / All Apps
            toggle on the right. Subtitle matches the Applications-page
            pattern ("{N} applications") so the header rhythm is
            consistent across pages — without it the left side is
            shorter than the toggle pill and items-center pushes the
            h1 down a couple of pixels relative to other pages. Toggle
            is admin/owner only and mirrors the Applications affordance;
            default is All Apps so the default experience is unchanged. */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Security Overview</h1>
            <p className="mt-1 text-sm text-gray-500">
              {displayMyApps && user.isUserAdmin()
                ? `Posture across ${visibleWebapps.length} application${visibleWebapps.length !== 1 ? "s" : ""} you own`
                : `Posture across ${visibleWebapps.length} application${visibleWebapps.length !== 1 ? "s" : ""}`}
            </p>
          </div>
          {user.isUserAdmin() && (
            <div className="flex items-center rounded-lg border border-gray-200 bg-gray-100 p-0.5 gap-0.5">
              {[
                { label: "My Apps", value: true },
                { label: "All Apps", value: false },
              ].map(({ label, value }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => setDisplayMyApps(value)}
                  className={[
                    "rounded-md px-3 py-1.5 text-sm font-medium transition-all",
                    displayMyApps === value
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-700",
                  ].join(" ")}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/*
          Active tests banner. role="status" + aria-live="polite" announces
          to screen readers when the count changes (test starts/finishes).
          aria-atomic ensures the full sentence is read each time, not
          just the diff — "3 tests currently running" rather than just "3".
        */}
        {activeTestApps.length > 0 && (
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50/70 px-4 py-3"
          >
            <span className="relative flex h-2.5 w-2.5 shrink-0" aria-hidden="true">
              {/*
                Pulsing dot is decorative; the textual count below carries
                the same information. motion-reduce:animate-none disables
                the infinite ping for users with prefers-reduced-motion set
                so vestibular triggers aren't fired by the dashboard.
              */}
              <span className="absolute inline-flex h-full w-full animate-ping motion-reduce:animate-none rounded-full bg-blue-400 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-blue-500" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-blue-800">
                {activeTestApps.length === 1
                  ? "1 test currently running"
                  : `${activeTestApps.length} tests currently running`}
              </p>
              <p className="text-xs text-blue-600 truncate">
                {activeTestApps.map((app) => app.name).join(", ")}
              </p>
            </div>
            <Activity className="h-4 w-4 text-blue-400 shrink-0" aria-hidden="true" />
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
          </div>
        )}

        {/* Empty state */}
        {isEmpty && (
          <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
            <Globe className="mx-auto h-10 w-10 text-gray-300" />
            <p className="mt-3 text-sm font-medium text-gray-700">No applications registered yet</p>
            <p className="mt-1 text-sm text-gray-500">Add your first application to start testing.</p>
            {user.isUserAdmin() && (
              <button
                type="button"
                onClick={() => navigate(ADD_APP_PAGE)}
                className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add Application
              </button>
            )}
          </div>
        )}

        {hasApps && (
          <>
            {/* Security summary — clicking a severity box scrolls to & filters the vuln table */}
            <SecuritySummaryCard
              summary={summary}
              totalApps={visibleWebapps.length}
              onCritClick={() => { setSeverityFilter(RISK_RATING_CRITICAL); scrollToVulnTable(); }}
              onHighClick={() => { setSeverityFilter(RISK_RATING_HIGH); scrollToVulnTable(); }}
              onMedClick={() => { setSeverityFilter(RISK_RATING_MED); scrollToVulnTable(); }}
              onLowClick={() => { setSeverityFilter(RISK_RATING_LOW); scrollToVulnTable(); }}
            />

            {/* Vulnerability category breakdown */}
            {categoryBreakdown.length > 0 && (
              <CategoryBreakdown categories={categoryBreakdown} />
            )}

            {/* Application cards */}
            <ApplicationCardsRow
              apps={sortedApps}
              sortKey={appSortKey}
              onSortChange={setAppSortKey}
              onNavigateToApp={navigateToApp}
            />

            {/* All clear */}
            {hasNoVulns && (
              <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
                <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-300" />
                <p className="mt-3 text-sm font-medium text-gray-700">No vulnerabilities found</p>
                <p className="mt-1 text-sm text-gray-500">
                  {listOfWebapps.some((app) => app.tests?.length > 0)
                    ? "No vulnerabilities in the latest test for any of your applications."
                    : "Run a test to start finding vulnerabilities."}
                </p>
              </div>
            )}

            {/* Open vulnerabilities — single unified table.
                scroll-mt-20 offsets the scroll target by 80px so the
                sticky Menubar (h-16 = 64px) doesn't cover the section
                heading when the user clicks a severity tile above. */}
            {!hasNoVulns && (
              <div ref={vulnTableRef} className="scroll-mt-20">
                <OpenVulnerabilitiesTable
                  filteredVulns={filteredVulns}
                  vulnCounts={vulnCounts}
                  severityFilter={severityFilter}
                  onFilterChange={setSeverityFilter}
                  onNavigateToVulns={navigateToVulns}
                />
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
}

// ----------------------------------------
//   SecuritySummaryCard
// ----------------------------------------

interface SummaryData {
  totalCrits: number;
  totalHighs: number;
  totalMeds: number;
  totalLows: number;
  unverifiedCount: number;
  latestTestDate: Date | null;
  latestTestAppName: string | null;
}

interface SecuritySummaryCardProps {
  summary: SummaryData;
  totalApps: number;
  onCritClick: () => void;
  onHighClick: () => void;
  onMedClick: () => void;
  onLowClick: () => void;
}

function SecuritySummaryCard({
  summary,
  totalApps,
  onCritClick,
  onHighClick,
  onMedClick,
  onLowClick,
}: SecuritySummaryCardProps) {
  const {
    totalCrits,
    totalHighs,
    totalMeds,
    totalLows,
    unverifiedCount,
    latestTestDate,
    latestTestAppName,
  } = summary;
  const verifiedCount = totalApps - unverifiedCount;

  return (
    <Card>
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="text-base font-semibold text-gray-900">Security Status</h2>
      </div>
      <CardContent className="p-6">

        {/* Severity boxes — clickable, scroll to section. focusRing
            shade is intentionally lighter (300) than the active-state
            ring on TestDetails Findings tiles (500): here it's an
            accessibility cue, there it indicates a selected filter. */}
        <div className="flex gap-3">
          {[
            { severity: RISK_RATING_CRITICAL, count: totalCrits, onClick: onCritClick, focusRing: "focus:ring-red-300"    },
            { severity: RISK_RATING_HIGH,     count: totalHighs, onClick: onHighClick, focusRing: "focus:ring-orange-300" },
            { severity: RISK_RATING_MED,      count: totalMeds,  onClick: onMedClick,  focusRing: "focus:ring-amber-300"  },
            { severity: RISK_RATING_LOW,      count: totalLows,  onClick: onLowClick,  focusRing: "focus:ring-yellow-300" },
          ].map(({ severity, count, onClick, focusRing }) => {
            const tile = getSeverityTileStyle(severity);
            return (
              <button
                key={severity}
                type="button"
                onClick={onClick}
                className={`flex flex-col items-center justify-center rounded-xl ${tile.bg} py-5 flex-1 ${tile.hoverBg} transition-colors focus:outline-none focus:ring-2 ${focusRing} focus:ring-offset-2`}
              >
                <p className={`text-3xl font-bold ${tile.numberText} tabular-nums leading-none`}>{count}</p>
                <p className={`text-xs font-medium ${tile.labelText} mt-1.5`}>{getSeverityLabel(severity)}</p>
              </button>
            );
          })}
        </div>

        {/* Metadata strip */}
        <div className="mt-4 grid grid-cols-2 divide-x divide-gray-100 rounded-xl border border-gray-100 bg-gray-50/60">
          <div className="px-5 py-4">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Last Test</p>
            {latestTestDate ? (
              <>
                <p className="mt-1 text-sm font-medium text-gray-900">{formatLongDate(latestTestDate)}</p>
                {latestTestAppName && (
                  <p className="text-xs text-gray-400">{latestTestAppName}</p>
                )}
              </>
            ) : (
              <p className="mt-1 text-sm text-gray-400">No tests run</p>
            )}
          </div>
          <div className="px-5 py-4">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Applications</p>
            <div className="mt-1 flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
              <p className="text-sm font-medium text-gray-900">{verifiedCount} ownership verified</p>
            </div>
            {unverifiedCount > 0 && (
              <div className="flex items-center gap-1.5 mt-0.5">
                <ShieldAlert className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                <p className="text-xs text-amber-600">{unverifiedCount} ownership pending</p>
              </div>
            )}
          </div>
        </div>

      </CardContent>
    </Card>
  );
}

// ----------------------------------------
//   ApplicationCardsRow
// ----------------------------------------

interface ApplicationCardsRowProps {
  apps: WebApplicationClass[];
  sortKey: AppSortKey;
  onSortChange: (key: AppSortKey) => void;
  onNavigateToApp: (webappid: string) => void;
}

function ApplicationCardsRow({ apps, sortKey, onSortChange, onNavigateToApp }: ApplicationCardsRowProps) {
  return (
    <Card>
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-4">
        <h2 className="text-base font-semibold text-gray-900">Applications</h2>
        <SortSelect<AppSortKey>
          id="app-sort"
          value={sortKey}
          onChange={onSortChange}
          options={APP_SORT_OPTIONS}
          ariaLabel="Sort applications"
        />
      </div>
      <CardContent className="p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {apps.map((app) => (
            <ApplicationCard key={app.id} app={app} onNavigate={onNavigateToApp} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ApplicationCard({ app, onNavigate }: { app: WebApplicationClass; onNavigate: (id: string) => void }) {
  const appColor  = app.color || deriveAppColor(app.name);
  const appAbbrev = app.abbrev || deriveAppAbbrev(app.name);
  const status    = app.webAppTestStatus;
  // Single source of truth for "is a run in flight?" — see
  // utils/liveTestState.ts. Replaces the former OR of app.testRunning
  // and an inlined tests.some(in-flight) scan, which existed only
  // because those two signals could disagree.
  const isRunning = isWebAppTestRunning(app);
  const hasTested = hasAppBeenTested(app);
  // Status pill label + tone derive from the latest terminal test
  // via the shared helper, so this card agrees with the TestDetails
  // badge on the webapp page and the TestSelector dropdown entries.
  // The Dashboard preload ships only the latest terminal test per
  // app, so tests[0] is the right input (unless tests[0] is itself
  // in-flight, in which case isRunning takes over below).
  const latestTerminal = app.tests?.find(
    (test) =>
      !!test.testStatus && TERMINAL_TEST_STATUSES.has(test.testStatus),
  );
  const resultLabel = getTestResultLabel({
    testStatus: latestTerminal?.testStatus,
    endTrigger: latestTerminal?.endTrigger,
  });
  // Severity counts come from the BE aggregate (webAppTestStatus.*)
  // but are zeroed when hasTested is false. Otherwise a stale or
  // failed-test aggregate would still surface severity pills on a
  // card whose status chip reads "Not Tested" — contradictory.
  const openCrits = hasTested ? (status?.opencrits ?? 0) : 0;
  const openHighs = hasTested ? (status?.openhighs ?? 0) : 0;
  const openMeds  = hasTested ? (status?.openmeds ?? 0) : 0;
  const openLows  = hasTested ? (status?.openlows ?? 0) : 0;
  const hasHistory = app.vulnHistory?.datapointsset;
  const isVerified = app.ownershipVerified;

  // Build sparkline data as one point per distinct vuln-count state.
  // Sums the weekly high/med/low arrays into a single total-per-week
  // series, then collapses carry-forward weeks (see
  // deriveTestEventSeries) so the chart's X-axis tracks test events
  // rather than calendar weeks — a user who tests quarterly gets 4
  // meaningful points per year instead of 49 flat weeks.
  const sparklineData = useMemo(() => {
    if (!hasHistory) return [];
    const highs = app.vulnHistory.highs;
    const meds  = app.vulnHistory.meds;
    const lows  = app.vulnHistory.lows;
    const len   = Math.max(highs.length, meds.length, lows.length);
    const weekly: number[] = [];
    for (let i = 0; i < len; i++) {
      weekly.push((highs[i] ?? 0) + (meds[i] ?? 0) + (lows[i] ?? 0));
    }
    return deriveTestEventSeries(weekly);
  }, [hasHistory, app.vulnHistory?.highs, app.vulnHistory?.meds, app.vulnHistory?.lows]);

  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow group"
      onClick={() => onNavigate(app.id)}
    >
      <CardContent className="p-4">
        {/* Top row: badge + name + status */}
        <div className="flex items-start gap-3">
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold text-white"
            style={{ backgroundColor: appColor }}
          >
            {appAbbrev}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-gray-900 truncate">{app.name}</p>
              <ChevronRight className="h-4 w-4 text-gray-300 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            {app.url && (
              <p className="text-xs text-gray-400 truncate">{app.url}</p>
            )}
          </div>
        </div>

        {/* Middle: severity pills. min-h reserves a fixed row height
            so cards with no open findings line up vertically with
            cards that have pills — keeps the spacing between the URL
            and the separator bar consistent across the grid. When a
            verified+tested app has zero open findings, surface that
            with a green "No findings" pill so the row isn't blank.

            While a test is in flight the whole row is suppressed: the
            counts here come from the last completed test's aggregate,
            which would be stale/misleading mid-run, and the green "No
            findings" pill would contradict the "Testing" status pill
            below. The Testing pill carries the state during a run; the
            counts reappear once the test terminates. */}
        <div className="mt-3 flex min-h-[24px] items-center justify-between">
          <div className="flex items-center gap-1.5">
            {!isRunning && (
              <>
                {[
                  { severity: RISK_RATING_CRITICAL, count: openCrits },
                  { severity: RISK_RATING_HIGH,     count: openHighs },
                  { severity: RISK_RATING_MED,      count: openMeds  },
                  { severity: RISK_RATING_LOW,      count: openLows  },
                ].map(({ severity, count }) => {
                  if (count === 0) return null;
                  const pill = getSeverityPillStyle(severity);
                  return (
                    <span
                      key={severity}
                      className={`inline-flex items-center rounded-full ${pill.bg} px-2 py-0.5 text-[10px] font-semibold ${pill.text}`}
                    >
                      {count} {getSeverityLabel(severity)}
                    </span>
                  );
                })}
                {isVerified &&
                  hasTested &&
                  openCrits === 0 &&
                  openHighs === 0 &&
                  openMeds === 0 &&
                  openLows === 0 && (
                    <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                      No findings
                    </span>
                  )}
              </>
            )}
          </div>
        </div>

        {/* Bottom: sparkline + status chip. The caption next to the
            sparkline spells out what the line represents — without it
            the chart reads as a generic decorative trace. When there's
            no trend data yet (fewer than 2 distinct test-event points)
            the whole sparkline + caption slot stays blank; an empty
            <span/> spacer keeps the status pill on the right via
            justify-between. */}
        {(() => {
          const sparklineTrend = getSparklineTrend(sparklineData);
          return (
            <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
              {sparklineTrend === "empty" ? (
                <span aria-hidden="true" />
              ) : (
                <div
                  className="flex items-center gap-2 min-w-0"
                  title="Open vulnerabilities across recent test events"
                >
                  <Sparkline data={sparklineData} />
                  <span className="text-[10px] text-gray-400 whitespace-nowrap">
                    {getSparklineTrendLabel(sparklineTrend)}
                  </span>
                </div>
              )}
              <AppStatusPill
                isVerified={isVerified}
                hasTested={hasTested}
                isRunning={isRunning}
                resultLabel={resultLabel}
              />
            </div>
          );
        })()}
      </CardContent>
    </Card>
  );
}

// ----------------------------------------
//   CategoryBreakdown
// ----------------------------------------

interface CategoryBreakdownProps {
  categories: { category: string; count: number }[];
}

function CategoryBreakdown({ categories }: CategoryBreakdownProps) {
  const totalVulns = categories.reduce((sum, c) => sum + c.count, 0);

  return (
    <Card>
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">Vulnerabilities by Category</h2>
        <span className="text-xs text-gray-400">{totalVulns} total</span>
      </div>
      <CardContent className="p-6">
        {/* Stacked horizontal bar */}
        <div className="flex h-2.5 w-full rounded-full overflow-hidden bg-gray-100 mb-4">
          {categories.map(({ category, count }) => (
            <div
              key={category}
              className="h-full first:rounded-l-full last:rounded-r-full"
              style={{
                width: `${(count / totalVulns) * 100}%`,
                backgroundColor: categoryColor(category),
              }}
              title={`${category}: ${count}`}
            />
          ))}
        </div>

        {/* Legend pills */}
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {categories.map(({ category, count }) => (
            <div key={category} className="flex items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full shrink-0"
                style={{ backgroundColor: categoryColor(category) }}
              />
              <span className="text-xs text-gray-600">{category}</span>
              <span className="text-xs font-semibold text-gray-900 tabular-nums">{count}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/** Deterministic color from category name — avoids collisions with the severity palette. */
const CATEGORY_COLORS = [
  "#6366f1", "#0ea5e9", "#8b5cf6", "#14b8a6", "#f97316",
  "#ec4899", "#64748b", "#84cc16", "#06b6d4", "#a855f7",
  "#f43f5e", "#22c55e", "#eab308", "#3b82f6", "#d946ef",
];
const categoryColorCache = new Map<string, string>();

function categoryColor(category: string): string {
  const cached = categoryColorCache.get(category);
  if (cached) return cached;

  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    hash = (hash * 31 + category.charCodeAt(i)) >>> 0;
  }
  const color = CATEGORY_COLORS[hash % CATEGORY_COLORS.length];
  categoryColorCache.set(category, color);
  return color;
}

// ----------------------------------------
//   OpenVulnerabilitiesTable — unified table with filter pills
// ----------------------------------------

interface OpenVulnerabilitiesTableProps {
  filteredVulns: FlatVuln[];
  vulnCounts: { critCount: number; highCount: number; medCount: number; lowCount: number };
  severityFilter: string | null;
  onFilterChange: (filter: string | null) => void;
  onNavigateToVulns: (webappid: string, testid: string, vulnid?: string) => void;
}

function OpenVulnerabilitiesTable({
  filteredVulns,
  vulnCounts,
  severityFilter,
  onFilterChange,
  onNavigateToVulns,
}: OpenVulnerabilitiesTableProps) {
  const { critCount, highCount, medCount, lowCount } = vulnCounts;
  const totalCount = critCount + highCount + medCount + lowCount;

  const filterPills: { label: string; value: string | null; count: number; activeStyle: string }[] = [
    { label: "All",      value: null,                 count: totalCount, activeStyle: "bg-gray-900 text-white" },
    { label: "Critical", value: RISK_RATING_CRITICAL, count: critCount,  activeStyle: getSeverityFilterActiveClass(RISK_RATING_CRITICAL) },
    { label: "High",     value: RISK_RATING_HIGH,     count: highCount,  activeStyle: getSeverityFilterActiveClass(RISK_RATING_HIGH) },
    { label: "Medium",   value: RISK_RATING_MED,      count: medCount,   activeStyle: getSeverityFilterActiveClass(RISK_RATING_MED) },
    { label: "Low",      value: RISK_RATING_LOW,      count: lowCount,   activeStyle: getSeverityFilterActiveClass(RISK_RATING_LOW) },
  ];

  return (
    <Card className="overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-base font-semibold text-gray-900">Vulnerabilities</h2>
        <div className="flex items-center gap-1.5">
          {filterPills.map((pill) => {
            if (pill.count === 0 && pill.value !== null) return null;
            const isActive = severityFilter === pill.value;
            return (
              <button
                key={pill.label}
                type="button"
                onClick={() => onFilterChange(isActive && pill.value !== null ? null : pill.value)}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors focus:outline-none ${
                  isActive
                    ? pill.activeStyle
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {pill.label}
                <span className={`tabular-nums ${isActive ? "opacity-80" : "text-gray-400"}`}>
                  {pill.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <CardContent className="p-0">
        {/* table-fixed locks column widths so they don't reflow as the
            severity filter changes (different rows mean different
            longest values per column). Risk widened to fit the full
            "Critical" label. */}
        <table className="w-full table-fixed">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-24">
                Risk
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-28 hidden lg:table-cell">
                Reference
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Vulnerability
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-52">
                Application
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-400 uppercase tracking-wider hidden md:table-cell w-32">
                First Found
              </th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-400 uppercase tracking-wider hidden xl:table-cell w-32">
                Last Seen
              </th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {filteredVulns.map(({ vuln, app, testId }) => (
              <DashboardVulnRow
                key={vuln.id}
                vuln={vuln}
                app={app}
                testId={testId}
                onNavigate={onNavigateToVulns}
              />
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

// ----------------------------------------
//   DashboardVulnRow
// ----------------------------------------

interface DashboardVulnRowProps {
  vuln: VulnerabilityClass;
  app: WebApplicationClass;
  testId: string;
  onNavigate: (webappid: string, testid: string, vulnid?: string) => void;
}

function DashboardVulnRow({ vuln, app, testId, onNavigate }: DashboardVulnRowProps) {
  const vulnRef  = vuln.reference || vuln.id.slice(0, 8);
  const appColor = app.color || deriveAppColor(app.name);
  const appAbbrev = app.abbrev || deriveAppAbbrev(app.name);
  const severityPill = getSeverityPillStyle(vuln.riskRating);
  const severityBadge = `${severityPill.bg} ${severityPill.text}`;
  const severityLabel = getSeverityLabel(vuln.riskRating);

  return (
    <tr
      className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/60 cursor-pointer transition-colors"
      onClick={() => onNavigate(app.id, testId, vuln.id)}
    >
      <td className="px-4 py-3">
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${severityBadge}`}>
          {severityLabel}
        </span>
      </td>
      <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap hidden lg:table-cell">
        {vulnRef}
      </td>
      <td className="px-4 py-3">
        <span className="text-sm font-medium text-gray-900 line-clamp-2">{vuln.title || vuln.vulnName}</span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs font-bold text-white"
            style={{ backgroundColor: appColor }}
          >
            {appAbbrev}
          </div>
          <span className="text-sm text-gray-700 truncate min-w-0">{app.name}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap hidden md:table-cell">
        {formatShortDate(vuln.dateFirstFound)}
      </td>
      <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap hidden xl:table-cell">
        {formatShortDate(vuln.dateLastSeen)}
      </td>
      <td className="px-4 py-3 w-8">
        <ChevronRight className="h-4 w-4 text-gray-300" />
      </td>
    </tr>
  );
}
